import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import { AggregatedScan } from '../../../common/model/dto/hormone-pattern/aggregated-scan';

function deltaPercent(current?: number | null, prev?: number | null) {
  if (current == null || prev == null) return null;
  return current - prev;
}

const OvHormonePatternTooltip: FC<{
  active?: boolean;
  label?: number;
  data: (AggregatedScan & { index: number })[];
}> = ({ active, label, data }) => {
  if (!active || !label || !data?.length) {
    return null;
  }

  const currentIndex = Math.max(0, Math.min(data.length - 1, label - 1));
  const currentDataPoint = data[currentIndex];

  const findPrevDataPoint = (
    index: number,
    key: keyof Pick<
      AggregatedScan,
      'lh_percentage' | 'pg_percentage' | 'lr_e3g_percentage'
    >
  ): number | null => {
    for (let i = index - 1; i >= 0; i--) {
      const val = (data[i] as any)[key];

      if (!!val) {
        return val;
      }
    }

    return null;
  };

  const items: Array<{
    key: 'lh' | 'pg' | 'lr_e3g';
    label: string;
    color: string;
    value?: number | null;
    pct?: number | null;
    prevPct?: number | null;
  }> = [
    {
      key: 'lh',
      label: 'LH',
      color: Colours.OV_DARK_GREEN,
      value: currentDataPoint.lh ?? null,
      pct: currentDataPoint.lh_percentage ?? null,
      prevPct: findPrevDataPoint(currentIndex, 'lh_percentage'),
    },
    {
      key: 'pg',
      label: 'PG',
      color: Colours.POST_OVULATION,
      value: currentDataPoint.pg ?? null,
      pct: currentDataPoint.pg_percentage ?? null,
      prevPct: findPrevDataPoint(currentIndex, 'pg_percentage'),
    },
    {
      key: 'lr_e3g',
      label: 'E3G',
      color: Colours.LIME_GREEN,
      value: currentDataPoint.lr_e3g ?? null,
      pct: currentDataPoint.lr_e3g_percentage ?? null,
      prevPct: findPrevDataPoint(currentIndex, 'lr_e3g_percentage'),
    },
  ];

  return (
    <TooltipContainer>
      <Header>{currentDataPoint.day ?? `#${currentDataPoint.index}`}</Header>
      {items.map((it) => {
        const delta = deltaPercent(it.pct, it.prevPct);
        const positive = delta != null && delta > 0;
        const negative = delta != null && delta < 0;
        const sign = positive ? '+' : negative ? '' : '';

        return (
          <TooltipRow key={it.key}>
            <NameCell>
              <SeriesDot color={it.color} /> {it.label}
            </NameCell>
            <ValueCell>{it.value != null ? it.value : '-'}</ValueCell>
            <TrendCell positive={positive} negative={negative}>
              {`${sign ?? ''} ${delta != null ? `${delta.toFixed(2)}%` : ''}`}
            </TrendCell>
          </TooltipRow>
        );
      })}
    </TooltipContainer>
  );
};

export default OvHormonePatternTooltip;

const TooltipContainer = styled.div`
  background: ${Colours.WHITE};
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
`;

const Header = styled.div`
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: ${Colours.OV_BASE};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0.125rem 0;
`;

const SeriesDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(p) => p.color};
  margin-right: 0.5rem;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  color: ${Colours.OV_BASE};
  font-weight: 600;
`;

const ValueCell = styled.div`
  color: ${Colours.OV_BASE};
`;

const TrendCell = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-weight: 600;
  color: ${(p) =>
    p.positive ? '#1a7f37' : p.negative ? '#b42318' : Colours.OV_BASE};
`;
